/**
 * Copy for the member-facing billing surfaces (FR-07, FR-39, FR-41).
 *
 * Feature-scoped rather than in `lib/constants/`, matching
 * `features/events/constants/events-strings.ts`: nothing outside
 * `features/billing/` renders these.
 *
 * ## Wording the tickets fixed, which the 2026-09-22 designs get wrong
 *
 * The upgrade screen in Figma says "investor tracking" and alternates
 * "DAX"/"DAXE". MYN-111.5 replaces the former with **"Due Diligence platform
 * access"**, and "DAXE" is correct everywhere. Precedent from the MYN-46
 * parity pass: ticket text beats the image. Do not restore either.
 *
 * ## v1.2: no "Upgrade to Premium" anywhere
 *
 * The data room is sold as an Issue Report (a one-time purchase) and granted
 * as SR NEXT Premium; the old Premium subscription is withdrawn (FR-128,
 * A-108). Data-room seats are never bought (FR-138), so every seat-purchase
 * string is gone too. Plan names come from the catalogue, not from here.
 */
export const BILLING_STRINGS = {
  PURCHASE: {
    TITLE: 'Get an Issue Report',
    /** A startup that already has access does not buy again (A-109). */
    ALREADY_SUBSCRIBED:
      'Your startup already has data room access through {plan}. See Billing & Info for the details.',
    GO_TO_BILLING: 'Go to Billing & Info',
    /** When the live plan has since left the catalogue and has no name to show. */
    PREMIUM_FALLBACK: 'your current plan',
    BENEFITS_TITLE: "What's included",
    BENEFIT_DATA_ROOM: 'Due Diligence platform access',
    BENEFIT_SEATS: '{count} data-room seats',
    BENEFIT_SEAT_ONE: '1 data-room seat, for you',
    /**
     * Above the plan name on each card. Not "Data room access": the client
     * found that confusing on an Issue Report purchase (UX round 3,
     * StartupAdminPatty X5), so it names the product line instead.
     */
    PLAN_EYEBROW: 'Investor Readiness',
    BILLED_MONTHLY: 'Billed monthly · Cancel anytime',
    BILLED_ANNUALLY: 'Billed annually · Cancel anytime',
    BILLED_ONCE: 'One payment · {term} of access',
    /** Choosing a plan leads to the review step, not straight to Stripe. */
    SELECT: 'Select',
    PROMO_LABEL: 'Promo code',
    PROMO_PLACEHOLDER: 'Optional',
    /** Card 9 step 2 wording (MYN-245). */
    SUBMIT: 'Continue to payment',
    REDIRECT_NOTE:
      "You'll be redirected to Stripe's secure checkout. My NEXT Raise does not store your card details.",
    CURRENT_PLAN_BADGE: 'Your current plan',
    EMPTY_TITLE: 'No plans are available right now',
    EMPTY_BODY: 'Nothing is on sale at the moment. Please check back, or contact support.',
    /** FR-203: nothing is on sale because the Issue Report plan is off. */
    COMING_SOON_TITLE: 'The Issue Report is coming soon',
    COMING_SOON_BODY: 'It is not on sale yet. Please check back soon.',
    PER_MONTH: '/month',
    PER_YEAR: '/year',
    ONE_TIME: 'one-time',
    /** A term in its own unit (FR-127): "7 days", "12 months", "1 day". */
    TERM_DAYS_ONE: '1 day',
    TERM_DAYS: '{count} days',
    TERM_MONTHS_ONE: '1 month',
    TERM_MONTHS: '{count} months',
  },
  /** The review step between choosing a plan and Stripe (Sprint 7 QA). */
  REVIEW: {
    TITLE: 'Review your purchase',
    INCLUDED_SEATS: '{count} data-room seats included',
    INCLUDED_SEAT_ONE: '1 data-room seat included',
    PROMO_LABEL: 'Promo code',
    PROMO_APPLY: 'Apply',
    PROMO_REMOVE: 'Remove',
    PROMO_APPLIED: '{code} applied',
    /** Shown when an applied code is refused while the order is re-priced. */
    PROMO_FAILED: 'That promo code could not be applied.',
    LINE_DISCOUNT: 'Discount ({code})',
    /** A blank promo duration discounts one billing period (Stripe `once`). */
    DISCOUNT_ONCE: 'Applies to your first payment only.',
    DISCOUNT_MONTHS: 'Applies for the first {count} months.',
    DUE_TODAY: 'Due today',
    /** After any limited discount ends; the interval suffix follows the amount. */
    RENEWS_AT: 'Then {amount}{interval}, until you cancel.',
    ONE_TIME_NOTE: 'One payment. {term} of access, with no renewal.',
    QUOTE_FAILED: 'We could not price this order. Please try again.',
    PLAN_UNAVAILABLE_TITLE: 'That plan is not available',
    PLAN_UNAVAILABLE_BODY: 'It may have been withdrawn. Choose another plan.',
  },
  RETURN: {
    /**
     * A-73. The webhook may not have landed, so this is a real state and not a
     * spinner: never tell a member their data room is ready seconds before it
     * refuses them.
     */
    PENDING_TITLE: 'Finalising your purchase',
    PENDING_BODY:
      "Stripe has taken your payment and we're applying it now. This usually takes a few seconds. You can safely stay on this page.",
    CONFIRMED_TITLE: 'Your purchase is confirmed',
    /** About the Issue Report, not "data room access" (UX round 3, StartupAdminPatty X10). */
    CONFIRMED_BODY: 'Your Issue Report is ready. Open your Data Room to upload your documents and start.',
    /** The receipt-style summary under the confirmation. */
    SUMMARY_PLAN: 'Plan',
    SUMMARY_AMOUNT: 'Amount paid',
    SUMMARY_SEATS: 'Data-room seats',
    NEXT_BILLING: 'Next billing date',
    /** A one-time purchase never bills again, so its date is when access ends. */
    ACCESS_ENDS: 'Access ends',
    /** The confirmation's main action (UX round 3, StartupAdminPatty Y12). */
    OPEN_DATA_ROOM: 'Open your Data Room',
    /**
     * Shown once polling gives up. The payment is not in doubt - Stripe took
     * it - only our confirmation is late, so the copy says where to look next
     * instead of leaving "finalising" on screen forever.
     */
    PENDING_TIMEOUT_TITLE: 'This is taking longer than usual',
    PENDING_TIMEOUT_BODY:
      'Your payment went through, but we have not finished applying it yet. Check Billing & Info in a few minutes, and contact support if your plan is still not active.',
    GO_TO_BILLING: 'Go to Billing & Info',
    CANCELLED_TITLE: 'Payment not completed',
    CANCELLED_BODY: 'You left checkout before paying, so no charge was made. You can pick a plan again whenever you are ready.',
    /**
     * A-75: hosted Checkout handles declines inside its own page and does not
     * redirect back with a decline code, so this degrades to a generic message
     * rather than promising the card-level specificity the old design showed.
     */
    FAILED_TITLE: 'We could not complete that purchase',
    FAILED_BODY:
      'The checkout session did not complete. Nothing was charged. Try again, and if it keeps happening contact support.',
    TRY_AGAIN: 'Try again',
    MISSING_SESSION: 'This page was opened without a checkout session, so there is nothing to show.',
  },
  OVERVIEW: {
    /** Empty-cell placeholder, as a constant rather than a literal in the table. */
    EMPTY_CELL: '-',
    /** Period cell for a one-time payment, whose invoice has no real range. */
    ONE_TIME_PERIOD: 'One-time payment',
    TITLE: 'Billing & Info',
    SUBTITLE: 'Your data room access, payments and receipts.',
    MEMBER_SINCE_LABEL: 'Member since',
    NEXT_BILL_LABEL: 'Estimated next bill',
    NEXT_BILL_VALUE: '{amount} on {date}',
    NEXT_BILL_CREDIT: 'Credit already on your account ({credit}) is taken off first.',
    NEXT_BILL_HELP: 'From Stripe, including any discount. Charged automatically while auto-renewal is on.',
    /** Beside the price on the plan card. */
    BILLED: { monthly: 'Billed monthly', annual: 'Billed annually', one_time: 'One-time payment' },
    AUTO_RENEW_ENABLED: 'Enabled',
    AUTO_RENEW_DISABLED: 'Off',
    INCLUDED_TITLE: "What's included",
    PLAN_SECTION: 'Current plan',
    STATUS_LABEL: 'Status',
    NEXT_BILLING_LABEL: 'Next billing date',
    /** A one-time plan does not renew, so the same date means something else (A-78). */
    ACCESS_ENDS_LABEL: 'Access ends',
    CANCELLED_NOTICE: 'Cancellation scheduled. Access continues until {date}.',
    /** Access that has fully ended is history, with one way forward. */
    ENDED_NOTICE: 'Your data room access ended on {date}. You can buy an Issue Report to open it again.',
    ACCESS_ENDED_LABEL: 'Access ended',
    SUBSCRIBE_AGAIN: 'Get an Issue Report',
    GRACE_NOTICE:
      'A payment has failed. Access continues until {date} while the card is retried.',
    MANAGE_PAYMENT: 'Manage payment method',
    MANAGE_PAYMENT_HELP: 'Opens Stripe to update the card on file.',
    AUTO_RENEW_LABEL: 'Auto-renewal',
    CANCEL_LINK: 'Cancel subscription',
    HISTORY_TITLE: 'Billing history',
    HISTORY_EMPTY: 'No invoices yet. The first appears once a payment has been taken.',
    DOWNLOAD: 'Download',
    DOWNLOAD_PENDING: 'Stripe is still finalising this invoice.',
    COL_DATE: 'Date',
    COL_PERIOD: 'Period',
    COL_AMOUNT: 'Amount',
    COL_STATUS: 'Status',
    COL_INVOICE: 'Invoice',
    // Never purchased (MYN-284): plain, no access warning or product pitch.
    NO_PURCHASES: 'No purchase history yet.',
    /** The header badge, named by what the startup holds now. */
    SERVICE_LABELS: {
      issue_report: 'Issue Report',
      // Client wording (MYN-289): no "Premium" or "Seed" in what the startup sees.
      premium: 'SR NEXT Financing Package',
      subscription: 'Subscription',
    },
  },
  /** The SR NEXT Premium package card (FR-136, MYN-257). No payment features at all. */
  PACKAGE: {
    TITLE: 'SR NEXT Financing Package',
    ENDS_LABEL: 'Access ends',
    DAYS_LEFT_LABEL: 'Days left',
    SEATS_LABEL: 'Data-room seats',
    SEATS_VALUE: '{count} seats',
    NOTE: 'No payment is needed. Seats are given and moved on Team Access.',
    MANAGE_SEATS: 'Manage seats',
    ENDED_TITLE: 'Your SR NEXT Financing Package has ended',
    ENDED_BODY: 'Your SR NEXT Financing Package access ended on {date}. You can buy an Issue Report to open the data room again.',
    GET_ISSUE_REPORT: 'Get an Issue Report',
  },
  CANCEL: {
    TITLE: 'Cancel your subscription?',
    /** A-74: access does not end at the click, so the body says when it does. */
    BODY: 'You keep access until the end of your current billing period. After that:',
    /** A-74: the exact date, beneath the list. */
    ENDS_ON: 'Access ends on {date}.',
    LOSE_TITLE: 'After that date you lose',
    LOSE_DAXE: 'Due Diligence platform access for everyone at your startup',
    LOSE_SEATS: 'All assigned data-room seats',
    KEEP_TITLE: 'You keep',
    KEEP_PROFILE: 'Your startup profile',
    KEEP_ACCOUNT: 'Your account and team',
    /** Sharing is free-tier, so links survive a lapse (2026-10-01). */
    KEEP_LINKS: 'Your shareable investor profile links',
    /** A-74 again: the subscription is reactivatable right up to the end date. */
    REACTIVATE_NOTE: 'You can turn renewal back on any time before {date}.',
    CONFIRM: 'Yes, cancel subscription',
    DISMISS: 'Keep my subscription',
  },
  AUTO_RENEW: {
    OFF_TITLE: 'Turn off auto-renewal?',
    OFF_BODY:
      'Your subscription will not renew after {date}. Access stays active until then, and you can turn renewal back on any time before that date.',
    OFF_CONFIRM: 'Yes, turn off',
    OFF_DISMISS: 'Keep auto-renewal on',
    ON_TITLE: 'Turn auto-renewal back on?',
    ON_BODY: 'Your subscription will renew as normal on {date}.',
    ON_CONFIRM: 'Yes, turn on',
    ON_DISMISS: 'Keep it off',
  },
  TOAST: {
    AUTO_RENEW_OFF: 'Auto-renewal is off. Access continues until the end of the period.',
    AUTO_RENEW_ON: 'Auto-renewal is back on.',
    CANCELLED: 'Cancellation scheduled. Access continues until the end of the period.',
    PORTAL_FAILED: 'We could not open Stripe just now. Please try again.',
  },
  STATUS_LABELS: {
    active: 'Active',
    past_due: 'Payment failed',
    grace_period: 'Payment failed',
    cancelled: 'Cancelled',
  },
  INVOICE_STATUS_LABELS: {
    draft: 'Draft',
    open: 'Open',
    paid: 'Paid',
    void: 'Void',
    uncollectible: 'Uncollectible',
  },
} as const;
